/**
 * ChalkQuizModal — naturalist's-notebook combat modal.
 *
 * Layout:
 *   - Full-screen slate-tinted backdrop with backdrop-blur (so the map
 *     remains as a faint memory behind the modal — disambiguates "this
 *     is a question, not a panel").
 *   - Centered paper card 600px wide / 700px max-height, slight rotation,
 *     pinned at the top by two strips of washi tape (PaperCard handles
 *     this with `tape`).
 *   - Header: "EXECUTE TEST" small caps (Fraunces) + territory-pair
 *     subtitle (e.g. "Brazil → Mexico") with chalk vermillion accent.
 *   - ClauseRef chip in Departure Mono — the academic-but-handcrafted hook.
 *   - Question prompt rendered with InkBleedText for the wet→dry reveal.
 *   - Choice tiles: lettered circle (A/B/C/D) + choice text. T/F kind
 *     renders just two big tiles ("TRUE" / "FALSE"). Submit is disabled
 *     until a tile is picked.
 *
 * Production data flow:
 *   - The modal is a pure projection of `attackState` from the server's
 *     authoritative GameState. Visibility is driven by `attackState.kind`:
 *       combatActive       → ask question
 *       combatResolved     → reveal hit/miss + explanation
 *       territoryConquered → cube-movement picker
 *     Other kinds render nothing.
 *   - `onSubmit(choiceIndex)` dispatches `ANSWER_QUESTION` upstream.
 *     There is NO local correct-answer comparison; the server resolves
 *     the answer and broadcasts the next attackState frame.
 *
 * All animation goes through Motion. The paper card's exit/entry uses a
 * gentle scale-and-opacity so it doesn't compete with the stamp scribble.
 */

import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import {
  type AttackState,
  type ClientSafeQuestion,
} from '@qd/protocol';
import { surfaces, fontStacks } from '@qd/design-tokens';
import { PaperCard } from '../base/PaperCard';
import { ChalkButton } from '../base/ChalkButton';
import { useIsMobile } from '../../hooks/useIsMobile';
import { InkBleedText } from './InkBleedText';
import { HitStamp, MissStamp } from './CombatResultStamp';
import { CombatTimerArc } from './CombatTimerArc';

/**
 * Public props. The modal stays mounted while combat is mid-flight; the
 * parent only ever passes the latest `attackState` and an explanation
 * string (when the server includes one in its reducer events). The
 * reveal animation is internal — we sequence stamp → explanation panel
 * locally, but never resolve hit/miss locally.
 */
interface ChalkQuizModalProps {
  /** Latest attack state from the server. Modal is hidden when 'idle' / 'sourceSelected' / 'targetSelected'. */
  readonly attackState: AttackState;
  /** Display names for the territories under attack (looked up from the active map). */
  readonly sourceName: string;
  readonly targetName: string;
  /** Per-turn timer in seconds. 0 disables the timer arc. Default 0. */
  readonly turnTimer?: number;
  /**
   * Dispatch ANSWER_QUESTION upstream. Called when the local player is
   * a COMBATANT — either the attacker or the defender. The reducer now
   * runs first-correct-wins logic and a wrong-answer cube penalty;
   * either side can win the combat by being faster + correct.
   */
  readonly onSubmit: (choiceIndex: number | boolean) => void;
  /**
   * Dispatch SUBMIT_LEARNING_ANSWER upstream. Called only when the
   * local player is a BYSTANDER (neither attacker nor defender). The
   * server records correctness and broadcasts via `learningAnswers`,
   * and rejects the action if a combatant tries to use it.
   */
  readonly onSubmitLearning?: (choiceIndex: number | boolean) => void;
  /**
   * Dispatch TURN_TIMEOUT upstream when the local timer hits zero with
   * no combatant submission. Server validates against the configured
   * session timer. Optional; if omitted, the legacy `onSubmit(-1)`
   * fallback fires (which only the attacker can dispatch).
   */
  readonly onTurnTimeout?: () => void;
  /**
   * Auto-dismiss callback the modal fires ~2.5s after `combatResolved`
   * lands. With the buzzer-race redesign there is no Continue button;
   * the resolved frame is a brief reveal (stamp + per-player feedback +
   * explanation) and then the parent dispatches END_COMBAT to advance
   * to idle. If BR-Backend ends up auto-transitioning combatResolved
   * server-side, this callback simply never fires (because the modal
   * never sees combatResolved long enough to schedule it).
   */
  readonly onCombatResolvedDismiss?: () => void;
  /** Dispatch MOVE_CUBES_AFTER_CONQUEST upstream after a conquest frame. */
  readonly onMoveCubes: (count: number) => void;
  /**
   * Bonus explanation panel shown alongside the hit/miss stamp. The
   * server can include a short paraphrase of the clause as part of
   * `combatResolved` (in v1 this is an empty string — the modal still
   * shows the stamp + clause ref).
   */
  readonly explanation?: string;
  /**
   * True when the local player IS the attacker. The attacker's submit
   * dispatches ANSWER_QUESTION (now first-correct-wins, not
   * attacker-only).
   */
  readonly isAttacker: boolean;
  /**
   * True when the local player IS the defender — i.e., they currently
   * control the territory under attack. Defenders see the same
   * question UI as attackers, dispatch ANSWER_QUESTION on submit, and
   * can WIN the combat by being faster + correct. Computed in the
   * parent off `gameState.territories[targetId].controllingPlayerId`.
   */
  readonly isDefender: boolean;
  /**
   * True when the attacker has already submitted a WRONG answer in the
   * current combat. The reducer applies a -1 cube penalty + locks them
   * out of re-submission. UI consequence: greyed-out tiles for the
   * attacker, "You answered — waiting for opponent" panel.
   * Stubbed `false` until BR-Backend's protocol fields land in main.
   */
  readonly attackerLockedOut?: boolean;
  /**
   * True when the defender has already submitted a WRONG answer in the
   * current combat. Same lockout semantics as `attackerLockedOut`.
   */
  readonly defenderLockedOut?: boolean;
  /** Display name of the attacker, shown to non-attackers in the waiting state. */
  readonly attackerName: string;
  /**
   * Local player's already-submitted answer for the in-flight question
   * (from `state.learningAnswers[questionId][selfId]`). Null if not yet
   * submitted. Drives the post-submit waiting panel for bystanders
   * and the per-player feedback at combatResolved. Combatants don't
   * use learningAnswers — their submission flows through ANSWER_QUESTION
   * and is reflected through the lockout flags + outcome.
   */
  readonly localLearningAnswer?: { choiceIndex: number | boolean; isCorrect: boolean } | null;
}

const ACTIVE_KINDS = new Set<AttackState['kind']>([
  'combatActive',
  'combatResolved',
  'territoryConquered',
]);

export function ChalkQuizModal({
  attackState,
  sourceName,
  targetName,
  turnTimer = 0,
  onSubmit,
  onSubmitLearning,
  onTurnTimeout,
  onCombatResolvedDismiss,
  onMoveCubes,
  explanation = '',
  isAttacker,
  isDefender,
  attackerLockedOut = false,
  defenderLockedOut = false,
  attackerName,
  localLearningAnswer = null,
}: ChalkQuizModalProps) {
  const visible = ACTIVE_KINDS.has(attackState.kind);
  return (
    <AnimatePresence>
      {visible ? (
        <ChalkQuizModalInner
          // Re-key on the question id (or attack id) so chained attacks
          // reset internal state without unmounting the modal.
          key={modalKey(attackState)}
          attackState={attackState}
          sourceName={sourceName}
          targetName={targetName}
          turnTimer={turnTimer}
          onSubmit={onSubmit}
          onSubmitLearning={onSubmitLearning ?? null}
          onTurnTimeout={onTurnTimeout ?? null}
          onCombatResolvedDismiss={onCombatResolvedDismiss ?? null}
          onMoveCubes={onMoveCubes}
          explanation={explanation}
          isAttacker={isAttacker}
          isDefender={isDefender}
          attackerLockedOut={attackerLockedOut}
          defenderLockedOut={defenderLockedOut}
          attackerName={attackerName}
          localLearningAnswer={localLearningAnswer}
        />
      ) : null}
    </AnimatePresence>
  );
}

function modalKey(s: AttackState): string {
  switch (s.kind) {
    case 'combatActive':
      return `q:${s.question.id}`;
    case 'combatResolved':
      return `r:${s.sourceId}:${s.targetId}:${s.committedCubes}`;
    case 'territoryConquered':
      return `c:${s.sourceId}:${s.targetId}`;
    default:
      return 'idle';
  }
}

interface ChalkQuizModalInnerProps {
  readonly attackState: AttackState;
  readonly sourceName: string;
  readonly targetName: string;
  readonly turnTimer: number;
  readonly onSubmit: (choiceIndex: number | boolean) => void;
  readonly onSubmitLearning: ((choiceIndex: number | boolean) => void) | null;
  readonly onTurnTimeout: (() => void) | null;
  readonly onCombatResolvedDismiss: (() => void) | null;
  readonly onMoveCubes: (count: number) => void;
  readonly explanation: string;
  readonly isAttacker: boolean;
  readonly isDefender: boolean;
  readonly attackerLockedOut: boolean;
  readonly defenderLockedOut: boolean;
  readonly attackerName: string;
  readonly localLearningAnswer: { choiceIndex: number | boolean; isCorrect: boolean } | null;
}

function ChalkQuizModalInner({
  attackState,
  sourceName,
  targetName,
  turnTimer,
  onSubmit,
  onSubmitLearning,
  onTurnTimeout,
  onCombatResolvedDismiss,
  onMoveCubes,
  explanation,
  isAttacker,
  isDefender,
  attackerLockedOut,
  defenderLockedOut,
  attackerName,
  localLearningAnswer,
}: ChalkQuizModalInnerProps) {
  const isMobile = useIsMobile();
  // The choice index the player has tentatively selected. For T/F we
  // store a boolean; for MCQ/scenario we store a numeric index. Reset
  // whenever the modal-key changes (chain attacks bring a new question).
  const [selected, setSelected] = useState<number | boolean | null>(null);

  // Reset local selection when the active question id changes.
  const activeQuestion = attackState.kind === 'combatActive' ? attackState.question : null;
  useEffect(() => {
    setSelected(null);
  }, [activeQuestion?.id]);

  // ─── Role + lockout derivations ───
  // Combatant = either side of the duel (attacker OR defender). Both
  // dispatch ANSWER_QUESTION; the reducer's first-correct-wins logic
  // decides the outcome, and a wrong answer flips the per-side lockout
  // flag + applies a -1 cube penalty.
  const isCombatant = isAttacker || isDefender;
  const localLockedOut =
    (isAttacker && attackerLockedOut) || (isDefender && defenderLockedOut);

  // Branch dispatch by role:
  //   - Combatant (and not yet locked out) submits ANSWER_QUESTION.
  //     Reducer applies first-correct-wins + wrong-answer cube penalty.
  //   - Bystander submits SUBMIT_LEARNING_ANSWER (advisory only). The
  //     reducer rejects this from a combatant.
  const handleSubmit = useCallback(() => {
    if (selected === null) return;
    if (isCombatant) {
      if (localLockedOut) return;
      onSubmit(selected);
    } else if (onSubmitLearning) {
      onSubmitLearning(selected);
    }
  }, [isCombatant, localLockedOut, onSubmit, onSubmitLearning, selected]);

  // Per-turn timer expiry: dispatch TURN_TIMEOUT (force-ends turn AND
  // miss-resolves combat in one action) when available. Falls back to
  // the legacy `onSubmit(-1)` ANSWER_QUESTION miss for the attacker
  // alone, so older callers that don't pass `onTurnTimeout` keep
  // working. Bystanders never trigger combat resolution from the timer.
  const handleTimerExpire = useCallback(() => {
    if (attackState.kind !== 'combatActive') return;
    if (onTurnTimeout) {
      onTurnTimeout();
      return;
    }
    if (isAttacker) onSubmit(-1);
  }, [attackState.kind, isAttacker, onSubmit, onTurnTimeout]);

  // ─── combatResolved auto-dismiss ───
  // No Continue / End Combat buttons in the buzzer-race redesign — the
  // resolved frame is a brief reveal (stamp + per-player feedback +
  // explanation), then we dispatch END_COMBAT to advance to idle. The
  // dismiss callback is a no-op for older clients (or if BR-Backend
  // ends up auto-transitioning combatResolved server-side).
  useEffect(() => {
    if (attackState.kind !== 'combatResolved') return;
    if (!onCombatResolvedDismiss) return;
    const id = window.setTimeout(() => {
      onCombatResolvedDismiss();
    }, 2500);
    return () => window.clearTimeout(id);
  }, [attackState.kind, onCombatResolvedDismiss]);

  // ─── Stamp-visibility ("winner") gate ───
  // outcome === 'hit' means the attacker landed (defender takes -1).
  // outcome === 'miss' means the defender held (attacker takes -1) —
  // this also covers the both-wrong / timeout case in the buzzer-race
  // model, where the defender wins by default. Show the HitStamp to the
  // winner of the round; show the MissStamp to the loser. Bystanders
  // see no stamp so the answer key isn't broadcast.
  const winnerStamp =
    attackState.kind === 'combatResolved'
      ? attackState.outcome === 'hit'
        ? isAttacker
          ? 'hit'
          : isDefender
            ? 'miss'
            : null
        : isDefender
          ? 'hit'
          : isAttacker
            ? 'miss'
            : null
      : null;

  return (
    <motion.div
      role="dialog"
      aria-modal="true"
      aria-labelledby="qd-combat-title"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.18 }}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1000,
        // Solid darker fill instead of backdrop-blur. Backdrop-filter
        // re-blurs whatever's underneath every frame; on Chromebooks /
        // weak GPUs that's a 30-50% FPS hit while the modal is up.
        // Higher opacity (94 vs 85) compensates for the missing blur
        // visually without the GPU cost.
        background: 'rgba(50, 42, 30, 0.55)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: isMobile ? 12 : 24,
      }}
    >
      <motion.div
        initial={{ y: 12, opacity: 0, scale: 0.985 }}
        animate={{ y: 0, opacity: 1, scale: 1 }}
        exit={{ y: 12, opacity: 0, scale: 0.985 }}
        transition={{ type: 'spring', stiffness: 380, damping: 28 }}
        style={{ position: 'relative', width: isMobile ? '100%' : undefined }}
      >
        <PaperCard
          tape={!isMobile}
          rotation={isMobile ? 0 : -1}
          padding={isMobile ? 18 : 32}
          style={{
            width: isMobile ? '100%' : 'min(640px, 92vw)',
            maxWidth: isMobile ? '100%' : 640,
            maxHeight: isMobile ? 'calc(100dvh - 24px)' : '90vh',
            boxShadow: isMobile
              ? '0 8px 24px rgba(0,0,0,0.32)'
              : '0 12px 32px rgba(0,0,0,0.42), inset -28px 0 36px -28px rgba(0,0,0,0.18)',
            display: 'block',
            overflowY: 'auto',
          }}
        >
          {/* Pencil cross-hatch on the left edge — naturalist notebook gutter. */}
          <span
            aria-hidden
            style={{
              position: 'absolute',
              left: 14,
              top: 18,
              bottom: 18,
              width: 22,
              background:
                'repeating-linear-gradient(135deg, rgba(58,56,51,0.12) 0 1px, transparent 1px 6px)',
              pointerEvents: 'none',
            }}
          />

          <ModalHeader sourceName={sourceName} targetName={targetName} />

          {attackState.kind === 'combatActive' ? (
            <CombatActiveBody
              question={attackState.question}
              turnTimer={turnTimer}
              selected={selected}
              onSelect={setSelected}
              onSubmit={handleSubmit}
              onTimerExpire={handleTimerExpire}
              bigChoices={isMobile}
              isAttacker={isAttacker}
              isDefender={isDefender}
              localLockedOut={localLockedOut}
              attackerName={attackerName}
              hasSubmitted={localLearningAnswer !== null}
              canSubmitLearning={onSubmitLearning !== null}
            />
          ) : attackState.kind === 'combatResolved' ? (
            <CombatResolvedBody
              outcome={attackState.outcome}
              explanation={explanation}
              isAttacker={isAttacker}
              isDefender={isDefender}
              winnerStamp={winnerStamp}
              localLearningAnswer={localLearningAnswer}
            />
          ) : attackState.kind === 'territoryConquered' ? (
            <TerritoryConqueredBody
              minCubes={attackState.mustMoveAtLeast}
              maxCubes={attackState.mustMoveAtMost}
              targetName={targetName}
              onMoveCubes={onMoveCubes}
              isAttacker={isAttacker}
              attackerName={attackerName}
            />
          ) : null}
        </PaperCard>

        {/*
          Stamp overlay during the resolved phase.

          PRIVACY GATE: rendered ONLY for the two combatants —
          attacker AND defender — and the stamp shown reflects
          THEIR side's outcome (HIT for the winner, MISS for the
          loser). Bystanders see nothing here so the answer key
          isn't broadcast (T/F questions especially: an explicit
          HIT for the attacker would hand bystanders the key).
          Bystanders still get their own private verdict via
          <PerPlayerFeedback /> in CombatResolvedBody.

          With the buzzer-race redesign the defender can WIN —
          when outcome === 'miss' (defender held / both wrong),
          the defender sees HitStamp and the attacker sees
          MissStamp. The combat outcome remains observable
          through the territory cube count — that's a public
          game-state consequence and is unavoidable. The fix is
          about the *explicit correctness signal* in the modal,
          not the game-state change itself.
        */}
        <AnimatePresence>
          {winnerStamp ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.18 }}
              style={{
                position: 'absolute',
                top: '40%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                pointerEvents: 'none',
              }}
            >
              {winnerStamp === 'hit' ? (
                <HitStamp size={200} />
              ) : (
                <MissStamp size={200} />
              )}
            </motion.div>
          ) : null}
        </AnimatePresence>
      </motion.div>
    </motion.div>
  );
}

interface CombatActiveBodyProps {
  readonly question: ClientSafeQuestion;
  readonly turnTimer: number;
  readonly selected: number | boolean | null;
  readonly onSelect: (value: number | boolean) => void;
  readonly onSubmit: () => void;
  readonly onTimerExpire: () => void;
  readonly bigChoices?: boolean;
  /**
   * True when the local player IS the attacker. Combatants (attacker
   * OR defender) dispatch ANSWER_QUESTION on submit; the reducer applies
   * first-correct-wins logic. Bystanders submit SUBMIT_LEARNING_ANSWER.
   */
  readonly isAttacker: boolean;
  /**
   * True when the local player IS the defender — they currently control
   * the territory under attack. Defenders see the same race UI as the
   * attacker and can win combat by being faster + correct.
   */
  readonly isDefender: boolean;
  /**
   * True when the local combatant has already submitted a wrong answer
   * in this combat. Greys out the choice tiles and surfaces the
   * "you answered" waiting panel instead of the Submit button.
   */
  readonly localLockedOut: boolean;
  readonly attackerName: string;
  /**
   * True when the local bystander has already submitted their learning
   * answer for this question. Drives the post-submit waiting panel.
   */
  readonly hasSubmitted: boolean;
  /**
   * True when the parent provided a SUBMIT_LEARNING_ANSWER dispatcher.
   * Older call sites that don't pass `onSubmitLearning` fall back to
   * the legacy "bystanders wait passively" behaviour.
   */
  readonly canSubmitLearning: boolean;
}

function CombatActiveBody({
  question,
  turnTimer,
  selected,
  onSelect,
  onSubmit,
  onTimerExpire,
  bigChoices,
  isAttacker,
  isDefender,
  localLockedOut,
  attackerName,
  hasSubmitted,
  canSubmitLearning,
}: CombatActiveBodyProps) {
  const isTrueFalse = question.kind === 'tf';
  const isScenario = question.kind === 'scenario';
  const isCombatant = isAttacker || isDefender;
  // Combatant who hasn't been locked out by a wrong answer: they get
  // active choice tiles + a Submit button. Bystanders see tiles only
  // before their first SUBMIT_LEARNING_ANSWER.
  const showChoiceUi = isCombatant
    ? !localLockedOut
    : canSubmitLearning && !hasSubmitted;

  return (
    <>
      <ClauseRef text={question.isoClauseRef} />
      {turnTimer > 0 ? (
        // Render the timer for everyone so bystanders understand the
        // urgency. Subdued for non-combatants (lower opacity) to signal
        // it's not their clock to act on directly — only a combatant's
        // expiry triggers TURN_TIMEOUT.
        <div style={{ marginTop: 14, marginBottom: -4 }}>
          <CombatTimerArc
            duration={turnTimer}
            onExpire={onTimerExpire}
            subdued={!isCombatant}
          />
        </div>
      ) : null}

      {/*
        Race-in-progress badge for combatants — hands the player a
        clear "this is a buzzer race; speed matters" signal. Bystanders
        don't see it (their UI is the lock-in button + waiting panel).
      */}
      {isCombatant && !localLockedOut ? (
        <RaceBadge isAttacker={isAttacker} />
      ) : null}

      <div
        style={{
          marginTop: 18,
          fontFamily: fontStacks.body,
          fontSize: isScenario ? 16 : 18,
          lineHeight: 1.55,
          color: surfaces.ink,
          paddingRight: isScenario ? 8 : 0,
        }}
      >
        <InkBleedText
          text={question.prompt}
          stagger={isScenario ? 0.012 : 0.024}
          delay={0.08}
        />
      </div>

      {showChoiceUi ? (
        <>
          <div style={{ marginTop: 22, display: 'flex', flexDirection: 'column', gap: 10 }}>
            {isTrueFalse ? (
              <TrueFalseChoices selected={selected} onPick={onSelect} />
            ) : (
              <MultipleChoiceChoices
                choices={question.choices ?? []}
                selected={typeof selected === 'number' ? selected : null}
                onPick={(idx) => onSelect(idx)}
                big={bigChoices}
              />
            )}
          </div>

          <div
            style={{
              marginTop: 24,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: 12,
              flexWrap: 'wrap',
            }}
          >
            <ChalkButton
              variant="primary"
              onClick={onSubmit}
              disabled={selected === null}
              ariaLabel="Submit answer"
            >
              {isCombatant ? 'Submit Answer' : 'Lock In My Pick'}
            </ChalkButton>
          </div>
        </>
      ) : isCombatant && localLockedOut ? (
        // Combatant submitted a wrong answer earlier in this combat —
        // reducer has applied a -1 cube penalty and locked them out.
        // The opponent can still race to a correct answer and win.
        <LockoutPanel />
      ) : hasSubmitted ? (
        // Bystander has already locked in their learning pick — show
        // a post-submit waiting panel that names the attacker and notes
        // the user is waiting for combat to resolve.
        <WaitingPanel
          attackerName={attackerName}
          message="and the defender are racing — your pick is locked in. Result coming up."
        />
      ) : (
        // canSubmitLearning is false (legacy call site or older client).
        // Fall back to the passive-observer behaviour.
        <WaitingPanel
          attackerName={attackerName}
          message="and the defender are racing for the answer. The result will appear here in a moment."
        />
      )}
    </>
  );
}

/**
 * "Race in progress" badge surfaced to combatants while the question
 * is open. Communicates the new buzzer-race semantic: fastest correct
 * answer wins; a wrong answer costs you a cube and locks you out for
 * the rest of this combat.
 */
function RaceBadge({ isAttacker }: { readonly isAttacker: boolean }) {
  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        marginTop: 14,
        padding: '8px 12px',
        border: `1.5px dashed ${surfaces.waxRed}`,
        borderRadius: 4,
        background: 'rgba(168, 57, 43, 0.06)',
        color: surfaces.ink,
        fontFamily: fontStacks.body,
        fontSize: 13,
        lineHeight: 1.4,
        display: 'flex',
        alignItems: 'center',
        gap: 10,
      }}
    >
      <span
        aria-hidden
        style={{
          display: 'inline-block',
          width: 9,
          height: 9,
          borderRadius: '50%',
          background: surfaces.waxRed,
          animation: 'qd-attacker-pulse 1.4s ease-in-out infinite',
          flex: '0 0 auto',
        }}
      />
      <span>
        <strong style={{ fontWeight: 700, color: surfaces.waxRed }}>
          {isAttacker ? 'Race in progress — attack' : 'Race in progress — defend'}
        </strong>{' '}
        — fastest correct answer wins. A wrong answer costs you a cube
        and locks you out of this combat.
      </span>
    </div>
  );
}

/**
 * Lockout panel rendered to a combatant who has submitted a wrong
 * answer. Tiles disappear; the player waits on the opponent. Visual
 * register matches the existing waiting/feedback panels (dashed border,
 * soft fill) so the modal stays cohesive.
 */
function LockoutPanel() {
  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        marginTop: 22,
        padding: '14px 16px',
        border: `1.5px dashed ${surfaces.graphite}`,
        borderRadius: 4,
        background: 'rgba(58, 56, 51, 0.08)',
        color: surfaces.ink,
        fontFamily: fontStacks.body,
        fontSize: 14,
        lineHeight: 1.5,
        opacity: 0.85,
      }}
    >
      <strong style={{ fontWeight: 700, color: surfaces.graphite }}>
        You answered.
      </strong>{' '}
      Your guess missed and cost you a cube — waiting on your opponent
      to lock in their answer.
    </div>
  );
}

interface CombatResolvedBodyProps {
  readonly outcome: 'hit' | 'miss';
  readonly explanation: string;
  /** True when the local player IS the attacker. Drives "your side" copy. */
  readonly isAttacker: boolean;
  /** True when the local player IS the defender. Drives "your side" copy. */
  readonly isDefender: boolean;
  /**
   * Which side the stamp overlay above is rendering for the local
   * player: 'hit' for the winner, 'miss' for the loser, null for
   * bystanders. Used to size the stamp placeholder area so the absolute
   * overlay has a place to land without colliding with the per-player
   * feedback panel.
   */
  readonly winnerStamp: 'hit' | 'miss' | null;
  /**
   * Local player's recorded learning answer for the just-resolved
   * question, if any. Renders the per-player feedback panel for
   * bystanders (combatants are reflected through the lockout flags +
   * cube outcome and don't carry a learningAnswer entry).
   */
  readonly localLearningAnswer: { choiceIndex: number | boolean; isCorrect: boolean } | null;
}

function CombatResolvedBody({
  outcome,
  explanation,
  isAttacker,
  isDefender,
  winnerStamp,
  localLearningAnswer,
}: CombatResolvedBodyProps) {
  const isMobile = useIsMobile();
  const isCombatant = isAttacker || isDefender;
  // Outcome copy from the local player's perspective — the buzzer-race
  // model means either side can win, so the defender now sees "your
  // defense held" instead of the legacy attacker-centric language.
  const outcomeCopy = outcomeMessageForLocal(outcome, isAttacker, isDefender);
  return (
    <>
      {/*
        Stamp landing area — reserved when the local player has a stamp
        (combatant), collapsed for bystanders so the per-player feedback
        sits flush under the prompt without an empty gap.
      */}
      {winnerStamp ? (
        <div
          style={{
            marginTop: 18,
            minHeight: isMobile ? 130 : 180,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            opacity: 0.4,
          }}
          aria-hidden
        />
      ) : null}
      {localLearningAnswer ? (
        <PerPlayerFeedback answer={localLearningAnswer} isAttacker={isAttacker} />
      ) : null}
      <div
        style={{
          marginTop: 24,
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: isMobile ? 'stretch' : 'center',
          justifyContent: 'space-between',
          gap: isMobile ? 16 : 12,
          flexWrap: 'wrap',
        }}
      >
        {explanation ? (
          <p
            style={{
              flex: 1,
              margin: 0,
              fontFamily: fontStacks.body,
              fontStyle: 'italic',
              fontSize: 13.5,
              lineHeight: 1.55,
              color: surfaces.ink,
              opacity: 0.82,
              textAlign: isMobile ? 'center' : 'left',
            }}
          >
            {explanation}
          </p>
        ) : (
          <span
            style={{
              flex: 1,
              fontFamily: fontStacks.body,
              fontSize: 14,
              color: surfaces.ink,
              opacity: 0.7,
              textAlign: isMobile ? 'center' : 'left',
            }}
          >
            {outcomeCopy}
          </span>
        )}
        {/*
          No Continue / End Combat buttons in the buzzer-race redesign.
          The modal auto-dismisses combatResolved after ~2.5s via the
          parent's onCombatResolvedDismiss callback. Combatants see a
          short "advancing…" hint; bystanders see the same so everyone
          knows the modal is about to close on its own.
        */}
        <span
          style={{
            fontFamily: fontStacks.mono,
            fontSize: 11,
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            color: surfaces.graphite,
            opacity: 0.7,
            textAlign: isMobile ? 'center' : 'right',
          }}
        >
          {isCombatant ? 'Combat resolved · advancing…' : 'Advancing…'}
        </span>
      </div>
    </>
  );
}

/**
 * Local-perspective outcome copy. Buzzer-race outcomes:
 *   - 'hit'  : attacker landed the answer first (defender takes -1).
 *   - 'miss' : defender held / both wrong / timeout (attacker takes -1).
 * We frame from the local player's seat so the message matches what
 * just happened on their map (their territory, their cubes).
 */
function outcomeMessageForLocal(
  outcome: 'hit' | 'miss',
  isAttacker: boolean,
  isDefender: boolean,
): string {
  if (outcome === 'hit') {
    if (isAttacker) return 'You answered first. Territory weakens.';
    if (isDefender) return 'Attacker landed the answer. You lose a cube.';
    return 'Attacker landed the answer.';
  }
  // outcome === 'miss'
  if (isDefender) return 'You held the line. Attacker loses a cube.';
  if (isAttacker) return 'Defender held. You lose a cube.';
  return 'Defender held the line.';
}

interface TerritoryConqueredBodyProps {
  readonly minCubes: number;
  readonly maxCubes: number;
  readonly targetName: string;
  readonly onMoveCubes: (count: number) => void;
  /**
   * When false, hide the cube-count picker + Confirm and show a waiting
   * panel. Only the attacker decides how many cubes occupy the conquered
   * territory; the defender (and any other observers) just watch.
   * Without this gate every connected client renders an interactive
   * picker that does nothing — the same "did my click do anything?"
   * confusion the combatActive gate already solves.
   */
  readonly isAttacker: boolean;
  readonly attackerName: string;
}

function TerritoryConqueredBody({
  minCubes,
  maxCubes,
  targetName,
  onMoveCubes,
  isAttacker,
  attackerName,
}: TerritoryConqueredBodyProps) {
  const [count, setCount] = useState<number>(minCubes);
  const clamped = Math.max(minCubes, Math.min(maxCubes, count));

  return (
    <>
      <div
        style={{
          marginTop: 18,
          fontFamily: fontStacks.body,
          fontSize: 16,
          lineHeight: 1.55,
          color: surfaces.ink,
        }}
      >
        Territory captured.{' '}
        {isAttacker ? (
          <>
            Move{' '}
            <strong style={{ color: surfaces.waxRed }}>{minCubes}</strong> to{' '}
            <strong style={{ color: surfaces.waxRed }}>{maxCubes}</strong>{' '}
            cubes forward into <em>{targetName}</em>.
          </>
        ) : (
          <>
            <strong>{attackerName}</strong> is choosing how many cubes to
            move into <em>{targetName}</em>.
          </>
        )}
      </div>

      {isAttacker ? (
        <>
          <div
            style={{
              marginTop: 16,
              display: 'flex',
              alignItems: 'center',
              gap: 10,
            }}
          >
            <input
              type="range"
              min={minCubes}
              max={maxCubes}
              value={clamped}
              onChange={(e) => setCount(Number(e.target.value))}
              aria-label="Cubes to move"
              style={{ flex: 1 }}
            />
            <span
              style={{
                minWidth: 40,
                fontFamily: fontStacks.mono,
                fontSize: 22,
                color: surfaces.waxRed,
                fontWeight: 700,
                textAlign: 'right',
              }}
            >
              {clamped}
            </span>
          </div>

          <div
            style={{
              marginTop: 24,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
            }}
          >
            <ChalkButton
              variant="primary"
              onClick={() => onMoveCubes(clamped)}
              ariaLabel="Confirm cube movement"
            >
              Move {clamped} {clamped === 1 ? 'Cube' : 'Cubes'}
            </ChalkButton>
          </div>
        </>
      ) : (
        // Non-attackers see the post-conquest header but no picker and no
        // Confirm button — the cube-count decision is the attacker's
        // alone. Visual register matches the combatActive waiting panel
        // (wax-red dashed border, pulsing red dot) so the experience
        // stays consistent across the whole combat flow.
        <WaitingPanel
          attackerName={attackerName}
          message={<>is moving cubes into <em>{targetName}</em>…</>}
        />
      )}
    </>
  );
}

/**
 * Per-player feedback panel rendered at combatResolved.
 *
 * Every player who engaged with the question sees their personal
 * verdict — the attacker has always seen hit/miss, but non-attackers
 * previously saw nothing. Visual register matches the rest of the
 * modal (paper card, ink text, wax-red for "wrong", muted-green for
 * "right"). Note: green is NOT in the locked design tokens because
 * the chalk-on-slate aesthetic restricts the palette; we use a soft
 * verdigris pigment fill that reads as "right" without breaking the
 * pencil-and-chalk register. The wax-red comes from `surfaces.waxRed`.
 */
interface PerPlayerFeedbackProps {
  readonly answer: { choiceIndex: number | boolean; isCorrect: boolean };
  readonly isAttacker: boolean;
}

function PerPlayerFeedback({ answer, isAttacker }: PerPlayerFeedbackProps) {
  const { isCorrect, choiceIndex } = answer;
  const accent = isCorrect ? 'rgba(63,122,110,0.85)' : surfaces.waxRed;
  const fill = isCorrect ? 'rgba(63,122,110,0.08)' : 'rgba(168,57,43,0.06)';
  const verdictWord = isCorrect ? 'Correct' : 'Incorrect';
  const choiceLabel = formatChoiceLabel(choiceIndex);

  return (
    <div
      style={{
        marginTop: 18,
        padding: '12px 16px',
        border: `1.5px dashed ${accent}`,
        borderRadius: 4,
        background: fill,
        color: surfaces.ink,
        fontFamily: fontStacks.body,
        fontSize: 14,
        lineHeight: 1.55,
        display: 'flex',
        alignItems: 'center',
        gap: 12,
      }}
    >
      <span
        aria-hidden
        style={{
          fontFamily: fontStacks.display,
          fontWeight: 700,
          fontSize: 22,
          color: accent,
          letterSpacing: '0.04em',
          flex: '0 0 auto',
        }}
      >
        {isCorrect ? '✓' : '✗'}
      </span>
      <span style={{ flex: 1 }}>
        <strong style={{ fontWeight: 700, color: accent }}>{verdictWord}.</strong>{' '}
        {isAttacker ? (
          isCorrect ? (
            <>Your answer landed.</>
          ) : (
            <>You picked <strong>{choiceLabel}</strong>.</>
          )
        ) : isCorrect ? (
          <>You picked <strong>{choiceLabel}</strong>.</>
        ) : (
          <>You picked <strong>{choiceLabel}</strong>. Review the clause and try again next round.</>
        )}
      </span>
    </div>
  );
}

function formatChoiceLabel(choiceIndex: number | boolean): string {
  if (typeof choiceIndex === 'boolean') return choiceIndex ? 'True' : 'False';
  if (choiceIndex < 0) return '—';
  return String.fromCharCode(65 + choiceIndex);
}

/**
 * Shared waiting-panel rendered to non-attackers across every gated
 * modal body (combatActive, combatResolved, territoryConquered). Single
 * visual register: wax-red dashed border, soft red fill, pulsing red dot
 * + a line that names the attacker and what they're doing.
 *
 * Consolidated here so all three gated bodies stay in sync if/when the
 * waiting visual gets tweaked. The pulse keyframe is injected via a
 * sibling <style> tag — duplicating it inside each body would still
 * work (CSS keyframes dedupe by name) but is needless markup churn.
 *
 * `marginTop` is configurable because the combatResolved variant lives
 * inside an existing flex row that already has its own spacing, while
 * combatActive / territoryConquered render the panel as a stand-alone
 * block under the prompt.
 */
interface WaitingPanelProps {
  readonly attackerName: string;
  /** The trailing clause after the bold attacker name, e.g. "is answering…". */
  readonly message: ReactNode;
  readonly marginTop?: number | undefined;
}

function WaitingPanel({ attackerName, message, marginTop = 22 }: WaitingPanelProps) {
  return (
    <div
      style={{
        marginTop,
        padding: '14px 16px',
        border: `1.5px dashed ${surfaces.waxRed}`,
        borderRadius: 4,
        background: 'rgba(168, 57, 43, 0.06)',
        color: surfaces.ink,
        fontFamily: fontStacks.body,
        fontSize: 14,
        lineHeight: 1.5,
        display: 'flex',
        alignItems: 'center',
        gap: 12,
      }}
    >
      <span
        aria-hidden
        style={{
          display: 'inline-block',
          width: 10,
          height: 10,
          borderRadius: '50%',
          background: surfaces.waxRed,
          animation: 'qd-attacker-pulse 1.4s ease-in-out infinite',
          flex: '0 0 auto',
        }}
      />
      <span>
        <strong style={{ fontWeight: 700 }}>{attackerName}</strong> {message}
      </span>
      <style>{`
        @keyframes qd-attacker-pulse {
          0%,100% { opacity: 1; transform: scale(1) }
          50%     { opacity: 0.45; transform: scale(1.35) }
        }
      `}</style>
    </div>
  );
}

interface ModalHeaderProps {
  readonly sourceName: string;
  readonly targetName: string;
}

function ModalHeader({ sourceName, targetName }: ModalHeaderProps) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
      <div>
        <h2
          id="qd-combat-title"
          style={{
            margin: 0,
            fontFamily: fontStacks.display,
            fontSize: 22,
            letterSpacing: '0.18em',
            fontVariantCaps: 'all-small-caps',
            fontVariationSettings: '"opsz" 96, "SOFT" 50',
            color: surfaces.waxRed,
            fontWeight: 700,
          }}
        >
          Execute Test
        </h2>
        <p
          style={{
            margin: '4px 0 0',
            fontFamily: fontStacks.body,
            fontSize: 16,
            color: surfaces.ink,
            opacity: 0.78,
          }}
        >
          {sourceName} <span style={{ opacity: 0.55, margin: '0 6px' }}>→</span> {targetName}
        </p>
      </div>
    </div>
  );
}

function ClauseRef({ text }: { text: string }) {
  return (
    <span
      style={{
        display: 'inline-block',
        marginTop: 12,
        padding: '4px 10px',
        border: `1px dashed ${surfaces.graphite}`,
        borderRadius: 2,
        fontFamily: fontStacks.mono,
        fontSize: 11,
        letterSpacing: '0.08em',
        color: surfaces.graphite,
        background: 'rgba(58,56,51,0.06)',
      }}
    >
      {text}
    </span>
  );
}

interface MCProps {
  readonly choices: readonly string[];
  readonly selected: number | null;
  readonly onPick: (idx: number) => void;
  readonly big?: boolean | undefined;
}

function MultipleChoiceChoices({ choices, selected, onPick, big }: MCProps) {
  return (
    <>
      {choices.map((choice, idx) => {
        const letter = String.fromCharCode(65 + idx); // A, B, C, D
        const isPicked = selected === idx;
        return (
          <ChoiceTile
            key={`${letter}-${idx}`}
            letter={letter}
            text={choice}
            picked={isPicked}
            big={big}
            onClick={() => onPick(idx)}
          />
        );
      })}
    </>
  );
}

interface TFProps {
  readonly selected: number | boolean | null;
  readonly onPick: (value: boolean) => void;
}

function TrueFalseChoices({ selected, onPick }: TFProps) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
      {([true, false] as const).map((value) => {
        const isPicked = selected === value;
        return (
          <ChoiceTile
            key={value ? 'T' : 'F'}
            letter={value ? 'T' : 'F'}
            text={value ? 'True' : 'False'}
            picked={isPicked}
            big
            onClick={() => onPick(value)}
          />
        );
      })}
    </div>
  );
}

interface ChoiceTileProps {
  readonly letter: string;
  readonly text: string;
  readonly picked: boolean;
  readonly big?: boolean | undefined;
  readonly onClick: () => void;
}

function ChoiceTile({ letter, text, picked, big, onClick }: ChoiceTileProps) {
  const border = picked ? surfaces.waxRed : surfaces.graphite;

  // Memoise hover props so the prop reference is stable across renders.
  const hoverProps = useMemo(
    () => ({
      whileHover: { x: [-1, 1, -1, 0] as [number, number, number, number], rotate: 0.4 },
      whileTap: { scale: 0.985 },
    }),
    [],
  );

  return (
    <motion.button
      type="button"
      onClick={onClick}
      {...hoverProps}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: big ? '20px 18px' : '12px 14px',
        background: picked ? 'rgba(168,57,43,0.08)' : 'rgba(255,255,255,0.42)',
        border: `1.6px solid ${border}`,
        borderRadius: 4,
        cursor: 'pointer',
        textAlign: 'left',
        fontFamily: fontStacks.body,
        fontSize: big ? 20 : 15,
        color: surfaces.ink,
        transition: 'border-color 200ms ease, background-color 200ms ease',
      }}
    >
      <span
        aria-hidden
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: big ? 36 : 28,
          height: big ? 36 : 28,
          borderRadius: '50%',
          border: `1.4px solid ${border}`,
          fontFamily: fontStacks.display,
          fontWeight: 700,
          fontSize: big ? 16 : 13,
          letterSpacing: '0.08em',
          color: border,
          flexShrink: 0,
          background: 'rgba(201,187,149,0.55)',
        }}
      >
        {letter}
      </span>
      <span style={{ flex: 1 }}>{text}</span>
    </motion.button>
  );
}
